import React from 'react';
import { CdrViewer } from '../CdrViewer';
import { API_BASE } from '../../lib/rpc';
import { crmClient } from '../../lib/crmRpc';
import { CrmConfig, formatNameUrl, formatPhoneUrl, formatDisplayHtml } from './crmUtils';

interface CrmCdrTabProps {
  activeTab: 'logs' | 'chat' | 'cdr' | 'voicemails';
  isLight: boolean;
  slug?: string;
  crm?: CrmConfig;
  serverConfig?: any;
  resolveCnamMap: (numbers: string[]) => Promise<Record<string, string>>;
  cdrAuthed: boolean;
  setCdrAuthed: (authed: boolean) => void;
  cdrPasswordInput: string;
  setCdrPasswordInput: (pw: string) => void;
  cdrAuthError: string;
  setCdrAuthError: (err: string) => void;
}

export function CrmCdrTab({
  activeTab,
  isLight,
  slug,
  crm,
  serverConfig,
  resolveCnamMap,
  cdrAuthed,
  setCdrAuthed,
  cdrPasswordInput,
  setCdrPasswordInput,
  cdrAuthError,
  setCdrAuthError
}: CrmCdrTabProps) {
  if (!crm?.enableCdr) return null;

  const isUnlocked = !crm.hasCdrPassword || cdrAuthed || (slug && sessionStorage.getItem(`crm-cdr-pw-${slug}`));

  return (
    <div
      style={{
        display: activeTab === 'cdr' ? 'flex' : 'none',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        width: '100%',
        background: isLight ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.02)',
        border: isLight ? '1px solid rgba(0,0,0,0.1)' : '1px solid rgba(255,255,255,0.05)',
        borderRadius: '12px',
        overflow: 'hidden',
        padding: '1.5rem',
        boxSizing: 'border-box'
      }}
    >
      {isUnlocked ? (
        <CdrViewer
          serverId={crm?.freepbxServerId || ''}
          isLight={isLight}
          resolveCnamMap={resolveCnamMap}
          crm={{ ...crm, serverConfig }}
          formatNameUrl={formatNameUrl}
          formatPhoneUrl={formatPhoneUrl}
          formatDisplayHtml={formatDisplayHtml}
          fetchCdrs={async (offset, limit, search, startDate, endDate, filters, skipCount, searchArchive) => {
            if (!slug) return { cdrs: [], total: 0 };
            const crmPw = localStorage.getItem(`crm-pw-${slug}`) || '';
            const cdrPw = crm?.hasCdrPassword ? (sessionStorage.getItem(`crm-cdr-pw-${slug}`) || cdrPasswordInput || '') : '';

            const query: any = { offset: String(offset), limit: String(limit), search };
            if (startDate) query.startDate = startDate;
            if (endDate) query.endDate = endDate;
            if (filters && filters.length > 0) query.filters = JSON.stringify(filters);
            if (skipCount) query.skipCount = 'true';
            if (searchArchive) query.archive = 'true';

            const res = await crmClient.api.v1['crm-dashboard'][':slug'].cdrs.$get(
              { param: { slug }, query },
              { headers: { 'x-crm-password': crmPw, 'x-crm-cdr-password': cdrPw } }
            );
            if (res.status === 401) {
              setCdrAuthed(false);
              sessionStorage.removeItem(`crm-cdr-pw-${slug}`);
              throw new Error('Unauthorized');
            }
            if (!res.ok) throw new Error('Failed to fetch CDRs');
            return await res.json();
          }}
          audioDownloadUrlPrefix={`${API_BASE.replace(/\/$/, '')}/api/v1/crm-dashboard/${slug}/files/download?x-crm-password=${encodeURIComponent(localStorage.getItem(`crm-pw-${slug}`) || '')}&x-crm-cdr-password=${encodeURIComponent(sessionStorage.getItem(`crm-cdr-pw-${slug}`) || cdrPasswordInput || '')}`}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <div style={{ background: isLight ? 'white' : 'rgba(0,0,0,0.3)', padding: '2.5rem', borderRadius: '12px', border: isLight ? '1px solid rgba(0,0,0,0.1)' : '1px solid rgba(255,255,255,0.1)', maxWidth: '400px', width: '100%', textAlign: 'center' }}>
            <h3 style={{ marginTop: 0, color: isLight ? '#09090b' : 'white', marginBottom: '1rem' }}>CDR Security</h3>
            <p style={{ color: isLight ? '#6b7280' : '#a1a1aa', fontSize: '0.9rem', marginBottom: '1.5rem' }}>Please enter the secondary password to access historical Call Detail Records and audio recordings.</p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setCdrAuthError('');
                if (!cdrPasswordInput.trim()) { setCdrAuthError('Password is required'); return; }
                if (slug) sessionStorage.setItem(`crm-cdr-pw-${slug}`, cdrPasswordInput);
                setCdrAuthed(true);
              }}
            >
              <input
                type="password"
                value={cdrPasswordInput}
                onChange={(e) => setCdrPasswordInput(e.target.value)}
                placeholder="CDR Access Password"
                style={{ width: '100%', padding: '0.75rem', background: isLight ? '#f9fafb' : 'rgba(0,0,0,0.5)', border: isLight ? '1px solid #e5e7eb' : '1px solid rgba(255,255,255,0.2)', color: isLight ? '#09090b' : 'white', borderRadius: '4px', marginBottom: '1rem', boxSizing: 'border-box' }}
              />
              {cdrAuthError && <div style={{ color: '#ef4444', fontSize: '0.85rem', marginBottom: '1rem' }}>{cdrAuthError}</div>}
              <button type="submit" style={{ width: '100%', padding: '0.75rem', background: '#38bdf8', color: '#09090b', border: 'none', borderRadius: '4px', fontWeight: 600, cursor: 'pointer' }}>
                Unlock Call History
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
